import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wand2,
  Copy,
  Check,
  Trash,
  Feather,
  ArrowRight,
  Loader2,
  Crown,
  Infinity as InfinityIcon,
  Zap,
  Eye,
  SplitSquareHorizontal,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ACTIONS, SAMPLES } from "@/lib/actions";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import UserMenu from "@/components/UserMenu";
import { DiffView } from "@/components/DiffView";
import {
  Tour,
  HOME_TOUR_STEPS,
  hasSeenTour,
  markTourSeen,
} from "@/components/OnboardingTour";
import {
  HistoryDesktop,
  HistoryMobileButton,
} from "@/components/HistorySidebar";

function wordCount(str) {
  if (!str) return 0;
  return str.trim().split(/\s+/).filter(Boolean).length;
}

function UsagePill({ usage, onUpgrade }) {
  if (!usage) return null;
  if (usage.is_pro) {
    return (
      <div
        data-testid="usage-pill"
        className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800"
      >
        <Crown className="h-3.5 w-3.5" />
        Pro · unlimited
      </div>
    );
  }
  const remaining = usage.remaining ?? Math.max(0, usage.limit - usage.used_today);
  const isEmpty = remaining <= 0;
  return (
    <button
      data-testid="usage-pill"
      onClick={onUpgrade}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
        isEmpty
          ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
          : "border-[#E6E2D8] bg-white text-slate-700 hover:border-indigo-300 hover:text-indigo-700"
      }`}
    >
      <Zap className="h-3.5 w-3.5" />
      {remaining} / {usage.limit} left today
      <span className="ml-1 hidden text-[10px] font-medium uppercase tracking-wider text-indigo-600 sm:inline">
        Upgrade
      </span>
    </button>
  );
}

export default function Home() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [action, setAction] = useState("fix_grammar");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);
  const [usage, setUsage] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  const [lastInput, setLastInput] = useState("");
  const [tourOpen, setTourOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const outputRef = useRef(null);

  const activeAction = useMemo(
    () => ACTIONS.find((a) => a.id === action),
    [action]
  );

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/history");
      setHistory(data.items || []);
    } catch (e) {
      console.error("history load failed", e);
    }
  };
  const loadUsage = async () => {
    try {
      const { data } = await api.get("/usage");
      setUsage(data);
    } catch (e) {
      console.error("usage load failed", e);
    }
  };

  useEffect(() => {
    loadHistory();
    loadUsage();
  }, []);

  // Open the tour on first visit, once usage has loaded so all targets exist
  useEffect(() => {
    if (!user || !usage) return;
    if (!hasSeenTour()) {
      const t = setTimeout(() => setTourOpen(true), 400);
      return () => clearTimeout(t);
    }
  }, [user, usage]);

  // Listen for a "restart tour" event from the user menu
  useEffect(() => {
    const handler = () => setTourOpen(true);
    window.addEventListener("clarityscribe:start-tour", handler);
    return () => window.removeEventListener("clarityscribe:start-tour", handler);
  }, []);

  const goUpgrade = () => navigate("/pricing");

  const handleGenerate = async () => {
    if (!input.trim()) {
      toast.error("Please paste or type some text first");
      return;
    }
    setLoading(true);
    setOutput("");
    setShowDiff(false);
    setCopied(false);
    try {
      const { data } = await api.post("/rewrite", { text: input, action });
      setOutput(data.output_text);
      setLastInput(input);
      if (data.usage) setUsage(data.usage);
      toast.success("Revision ready");
      loadHistory();
      setTimeout(() => {
        outputRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 100);
    } catch (e) {
      const status = e?.response?.status;
      const msg =
        e?.response?.data?.detail || "Something went wrong. Please try again.";
      if (status === 402) {
        toast.error(String(msg), {
          action: {
            label: "Upgrade",
            onClick: goUpgrade,
          },
        });
      } else {
        toast.error(String(msg));
      }
      loadUsage();
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  const handleClear = () => {
    setInput("");
    setOutput("");
    setCopied(false);
  };

  const handleUseSample = (s) => {
    setInput(s);
    setOutput("");
  };

  const handleRestore = (item) => {
    setInput(item.input_text);
    setLastInput(item.input_text);
    setOutput(item.output_text);
    setAction(item.action);
    setShowDiff(false);
    toast.success("Restored from history");
  };

  const handleDeleteItem = async (id) => {
    try {
      await api.delete(`/history/${id}`);
      setHistory((h) => h.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Could not delete");
    }
  };

  const handleClearAll = async () => {
    try {
      await api.delete("/history");
      setHistory([]);
      toast.success("History cleared");
    } catch {
      toast.error("Could not clear");
    }
  };

  const outOfQuota =
    usage && !usage.is_pro && (usage.remaining ?? 0) <= 0;

  return (
    <div className="min-h-screen bg-[#FAF9F5]">
      {/* Header */}
      <header className="border-b border-[#E6E2D8]/60 bg-[#FAF9F5]/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#1A1D20] text-[#FAF9F5]">
              <Feather className="h-4 w-4" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold leading-none text-[#1A1D20]">
                Write It Easy AI
              </h1>
              <p className="mt-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">
                AI writing assistant
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:block">
              <UsagePill usage={usage} onUpgrade={goUpgrade} />
            </div>
            <div className="lg:hidden">
              <HistoryMobileButton
                items={history}
                onRestore={handleRestore}
                onDelete={handleDeleteItem}
                onClear={handleClearAll}
              />
            </div>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
        <div className="flex gap-6">
          <HistoryDesktop
            items={history}
            onRestore={handleRestore}
            onDelete={handleDeleteItem}
            onClear={handleClearAll}
          />

          <div className="min-w-0 flex-1 space-y-6">
            {/* Hero */}
            <section className="fade-up">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-indigo-700">
                    {user?.is_pro ? "Pro workspace" : "Write with confidence"}
                  </p>
                  <h2 className="font-heading text-3xl font-extrabold leading-[1.05] tracking-tight text-[#1A1D20] sm:text-4xl lg:text-5xl">
                    Hi {user?.name?.split(" ")[0] || "there"},
                    <br />
                    <span className="text-indigo-700">
                      what should we polish today?
                    </span>
                  </h2>
                </div>
                <div className="sm:hidden">
                  <UsagePill usage={usage} onUpgrade={goUpgrade} />
                </div>
              </div>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-600">
                A friendly AI helper for grammar, clarity, tone, length, and
                brainstorms — built for anyone who just wants their writing to
                sound better.
              </p>
            </section>

            {/* Quota banner when out */}
            {outOfQuota && (
              <div
                data-testid="quota-banner"
                className="fade-up flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white">
                    <Crown className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#1A1D20]">
                      You've used all {usage.limit} free rewrites today
                    </p>
                    <p className="text-xs text-slate-600">
                      Upgrade to Pro for unlimited rewrites at $9/month.
                    </p>
                  </div>
                </div>
                <Button
                  data-testid="upgrade-cta-banner"
                  onClick={goUpgrade}
                  className="h-10 gap-2 rounded-full bg-[#1A1D20] px-4 text-sm font-semibold text-white hover:bg-[#2A2E3D]"
                >
                  <InfinityIcon className="h-4 w-4" />
                  Go unlimited
                </Button>
              </div>
            )}

            {/* Action picker */}
            <section className="fade-up" style={{ animationDelay: "60ms" }}>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  1. Pick what you want to do
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="actions-grid">
                {ACTIONS.map((a) => {
                  const Icon = a.icon;
                  const active = a.id === action;
                  return (
                    <button
                      key={a.id}
                      data-testid={`action-button-${a.id}`}
                      onClick={() => setAction(a.id)}
                      className={`group relative flex flex-col items-start gap-2 rounded-2xl border p-3 text-left transition-all duration-200 active:scale-[0.98] ${
                        active
                          ? "border-[#1A1D20] bg-[#1A1D20] text-white shadow-[0_8px_24px_-8px_rgba(26,29,32,0.4)]"
                          : "border-[#E6E2D8] bg-white hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_6px_18px_-8px_rgba(0,0,0,0.08)]"
                      }`}
                    >
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                          active ? "bg-white/10" : a.bg
                        }`}
                      >
                        <Icon
                          className={`h-4 w-4 ${active ? "text-white" : a.text}`}
                        />
                      </div>
                      <div className="min-w-0">
                        <div
                          className={`text-sm font-semibold leading-snug ${
                            active ? "text-white" : "text-[#1A1D20]"
                          }`}
                        >
                          {a.label}
                        </div>
                        <div
                          className={`mt-0.5 hidden text-xs leading-snug sm:block ${
                            active ? "text-white/70" : "text-slate-500"
                          }`}
                        >
                          {a.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Editor grid */}
            <section
              className="fade-up grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6"
              style={{ animationDelay: "120ms" }}
            >
              {/* Input */}
              <div className="rounded-2xl border border-[#E6E2D8] bg-white p-4 shadow-[0_4px_20px_-12px_rgba(0,0,0,0.06)] sm:p-5">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    2. Your text
                  </p>
                  <div className="flex items-center gap-2 text-[11px] text-slate-400">
                    <span>{wordCount(input)} words</span>
                    {input && (
                      <button
                        data-testid="clear-text-button"
                        onClick={handleClear}
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-slate-500 hover:bg-slate-100 hover:text-red-600"
                      >
                        <Trash className="h-3 w-3" /> Clear
                      </button>
                    )}
                  </div>
                </div>
                <Textarea
                  data-testid="input-text-area"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Paste or type your text here…"
                  className="min-h-[220px] resize-y rounded-xl border-[#E6E2D8] bg-[#FDFCF8] font-body text-[15px] leading-relaxed text-slate-800 placeholder:text-slate-400 focus-visible:ring-indigo-500/40 sm:min-h-[280px]"
                />

                <div className="mt-3">
                  <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-slate-400">
                    Or try a sample
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {SAMPLES.map((s, i) => (
                      <button
                        key={i}
                        data-testid={`sample-prompt-button-${i}`}
                        onClick={() => handleUseSample(s)}
                        className="max-w-full truncate rounded-full border border-[#E6E2D8] bg-[#F3F1EA] px-3 py-1 text-xs text-slate-600 transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-800"
                        title={s}
                      >
                        {s.slice(0, 48)}
                        {s.length > 48 ? "…" : ""}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between gap-3">
                  <Badge
                    variant="secondary"
                    className="hidden gap-1.5 rounded-full border border-[#E6E2D8] bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-white sm:inline-flex"
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ background: activeAction?.dot }}
                    />
                    {activeAction?.short}
                  </Badge>
                  <Button
                    data-testid="generate-ai-button"
                    onClick={handleGenerate}
                    disabled={loading || !input.trim()}
                    className="group ml-auto h-11 gap-2 rounded-full bg-[#1A1D20] px-5 text-sm font-semibold text-[#FAF9F5] shadow-[0_8px_24px_-8px_rgba(26,29,32,0.5)] hover:bg-[#2A2E3D] active:scale-[0.98] disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Working…
                      </>
                    ) : (
                      <>
                        <Wand2 className="h-4 w-4" />
                        Generate
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Output */}
              <div
                ref={outputRef}
                data-testid="output-text-container"
                className="flex flex-col rounded-2xl border border-[#E6E2D8] bg-[#F3F1EA]/60 p-4 sm:p-5"
              >
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    3. Result
                  </p>
                  <div className="flex items-center gap-2 text-[11px] text-slate-400">
                    {output && !loading && lastInput && action !== "generate_ideas" && (
                      <div className="flex items-center overflow-hidden rounded-full border border-[#E6E2D8] bg-white">
                        <button
                          data-testid="view-toggle-result"
                          onClick={() => setShowDiff(false)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                            !showDiff
                              ? "bg-[#1A1D20] text-white"
                              : "text-slate-500 hover:text-slate-800"
                          }`}
                        >
                          <Eye className="h-3 w-3" />
                          Result
                        </button>
                        <button
                          data-testid="view-toggle-diff"
                          onClick={() => setShowDiff(true)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                            showDiff
                              ? "bg-[#1A1D20] text-white"
                              : "text-slate-500 hover:text-slate-800"
                          }`}
                        >
                          <SplitSquareHorizontal className="h-3 w-3" />
                          Diff
                        </button>
                      </div>
                    )}
                    <span>{wordCount(output)} words</span>
                  </div>
                </div>

                {loading ? (
                  <div className="space-y-2">
                    <div className="shimmer h-4 w-4/5 rounded" />
                    <div className="shimmer h-4 w-full rounded" />
                    <div className="shimmer h-4 w-11/12 rounded" />
                    <div className="shimmer h-4 w-3/4 rounded" />
                    <div className="shimmer h-4 w-2/3 rounded" />
                  </div>
                ) : output ? (
                  <>
                    {showDiff && lastInput ? (
                      <DiffView original={lastInput} revised={output} />
                    ) : (
                      <div
                        data-testid="output-text-area"
                        className="thin-scroll max-h-[420px] flex-1 overflow-y-auto whitespace-pre-wrap rounded-xl bg-white p-4 font-body text-[15px] leading-relaxed text-slate-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
                      >
                        {output}
                      </div>
                    )}
                    {showDiff && lastInput && (
                      <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
                        <span className="inline-flex items-center gap-1">
                          <span className="h-2 w-2 rounded-sm bg-emerald-200" />
                          Added
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <span className="h-2 w-2 rounded-sm bg-red-200" />
                          Removed
                        </span>
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-end">
                      <Button
                        data-testid="copy-result-button"
                        onClick={handleCopy}
                        variant="outline"
                        className="h-10 gap-2 rounded-full border-[#1A1D20] bg-white px-4 text-sm font-semibold text-[#1A1D20] hover:bg-[#1A1D20] hover:text-white"
                      >
                        {copied ? (
                          <>
                            <Check className="h-4 w-4" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            Copy result
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-[#E6E2D8] bg-white/50 p-8 text-center">
                    <div>
                      <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
                        <Wand2 className="h-5 w-5" />
                      </div>
                      <p className="text-sm font-medium text-slate-600">
                        Your revised text will appear here
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        Paste text, pick an action, then hit Generate
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </section>

            <footer className="pb-6 pt-4 text-center text-[11px] text-slate-400">
              Built with care for beginner writers · Your history is private to
              your account
            </footer>
          </div>
        </div>
      </main>
      <Tour
        open={tourOpen}
        steps={HOME_TOUR_STEPS}
        onClose={() => {
          setTourOpen(false);
          markTourSeen();
        }}
      />
    </div>
  );
}
